import { handleActions, combineActions } from 'redux-actions';
import { combineReducers } from 'redux';
import * as orderActionTypes from 'order/details/containers/details-constants';
import { publishSteps } from 'utils/app-configs';
import * as equipmentActionTypes from 'equipment/info/containers/info-constants';
import * as installedAtActionTypes from 'installed-at/info/containers/constants';
import * as dashboardActionTypes from 'common/dashboard/containers/constants';
import * as lockActionTypes from './publish-steps-constants';
import * as ganttActionTypes from '../../gantt/containers/gantt-constants';
import { AUTO_PLANNING_ACTION_SUCCESS } from '../../toolbar/containers/constants';

export const operationInfo = handleActions(
  {
    [combineActions(
      lockActionTypes.RESET_OPERATION_INFO,
      'LOCATION_CHANGE',
    )]: () => ({}),
    [combineActions(
      orderActionTypes.GET_ORDER_DETAILS_SUCCESS,
      orderActionTypes.SAVE_ORDER_SUCCESS,
      equipmentActionTypes.GET_EQUIPMENT_INFO_SUCCESS,
      equipmentActionTypes.SAVE_EQUIPMENT_INFO_SUCCESS,
      installedAtActionTypes.INSTALLED_AT_INFO_SUCCESS,
      installedAtActionTypes.INSTALLED_AT_INFO_SAVE_SUCCESS,
      installedAtActionTypes.VERIFY_INSTALLED_AT_INFO_SUCCESS,
      dashboardActionTypes.SAVE_NOTES_SUCCESS,
      dashboardActionTypes.DASHBOARD_SUCCESS,
      lockActionTypes.ABORT_SUCCESS,
    )]: (state, { payload }) => payload.operationInfo,
    [lockActionTypes.CURRENT_STATUS_SUCCESS]: (state, { payload }) =>
      payload.status !== publishSteps.waitCycle.status ? payload : state,
  },
  {},
);

export const shouldPoll = handleActions(
  {
    [combineActions(
      lockActionTypes.CURRENT_STATUS_FAILURE,
      lockActionTypes.STOP_PUBLISH_STEP_POLL,
    )]: () => false,
    [lockActionTypes.START_PUBLISH_STEP_POLL]: () => true,
    [lockActionTypes.CURRENT_STATUS_SUCCESS]: (state, { payload }) => {
      const lowestStatus = payload.allowUnknown ? -1 : 0;
      if (
        payload.status > lowestStatus &&
        (
          payload.status < publishSteps.done.status ||
          payload.status === publishSteps.waitCycle.status
        ) &&
        !payload.isTimedOut &&
        !payload.isStale &&
        payload.errors.length === 0
      ) {
        return true;
      }
      return false;
    },
  },
  false,
);

export const shouldMultiPoll = handleActions(
  {
    [combineActions(
      lockActionTypes.MULTI_STATUS_FAILURE,
      lockActionTypes.STOP_MULTI_STATUS_POLL,
    )]: () => false,
    [lockActionTypes.START_MULTI_STATUS_POLL]: () => true,
  },
  false,
);

export const multiStatusInfo = handleActions(
  {
    [combineActions(
      lockActionTypes.MULTI_STATUS_FAILURE,
      lockActionTypes.STOP_MULTI_STATUS_POLL,
    )]: () => [],
    [lockActionTypes.MULTI_STATUS_SUCCESS]: (state, { payload }) => payload.items,
    [combineActions(
      ganttActionTypes.PUBLISH_ALL_TO_SAP_SUCCESS,
      ganttActionTypes.PUBLISH_TO_SAP_SUCCESS,
    )]: (state, { payload }) =>
        state?.map((i) => {
          const updatedItem = payload.succeeded?.find(p => p.id === i.id);
          if (updatedItem) {
            return {
              ...i,
              operationInfo: updatedItem.operationInfo,
            };
          }
          return i;
        }),
    [AUTO_PLANNING_ACTION_SUCCESS]: (state, { payload }) =>
      state?.map((i) => {
        const updatedItem = payload.successfulBlocks?.find(p => p.id === i.id);
        if (updatedItem) {
          return {
            ...i,
            operationInfo: updatedItem.operationInfo,
          };
        }
        return i;
      }),
    [combineActions(
      ganttActionTypes.MARK_DATE_CONFIRMED_SUCCESS,
      ganttActionTypes.DELETE_BLOCK_SUCCESS,
    )]: (state, { payload }) =>
        state?.map((i) => {
          if (i.id === payload.id) {
            return {
              ...i,
              operationInfo: payload.operationInfo || {},
            };
          }
          return i;
        }),
  },
  [],
);

export const idsToPoll = handleActions(
  {
    [combineActions(
      lockActionTypes.STOP_MULTI_STATUS_POLL,
    )]: () => [],
    [lockActionTypes.START_MULTI_STATUS_POLL]: (state, { payload }) => payload,
    [lockActionTypes.SET_IDS_TO_POLL]: (state, { payload }) => payload,
  },
  [],
);

export const multiPollingInterval = handleActions(
  {
    [lockActionTypes.RESET_MULTI_POLLING]: (state, { payload }) =>
      payload.shouldReset ? payload.pollingInterval : state,
    [lockActionTypes.MULTI_STATUS_SUCCESS]: (state, { payload }) => payload.pollingInterval,
  },
  10,
);

export const shouldResetMultiPolling = handleActions(
  {
    [lockActionTypes.RESET_MULTI_POLLING]: (state, { payload }) => payload.shouldReset,
  },
  false,
);


export const isLoading = handleActions(
  {
    [combineActions(
      lockActionTypes.UNLOCK_LOCK_REQUEST,
      lockActionTypes.DISCARD_LOCK_REQUEST,
    )]: () => true,
    [combineActions(
      lockActionTypes.UNLOCK_LOCK_SUCCESS,
      lockActionTypes.UNLOCK_LOCK_FAILURE,
      lockActionTypes.DISCARD_LOCK_SUCCESS,
      lockActionTypes.DISCARD_LOCK_FAILURE,
    )]: () => false,
  },
  false,
);

export const isAborting = handleActions(
  {
    [combineActions(
      lockActionTypes.ABORT_REQUEST,
    )]: () => true,
    [combineActions(
      lockActionTypes.ABORT_SUCCESS,
      lockActionTypes.ABORT_FAILURE,
    )]: () => false,
  },
  false,
);

export const abortFailure = handleActions(
  {
    [lockActionTypes.ABORT_SUCCESS]: (state, { payload }) => !payload.abortSuccessful,
    [combineActions(
      'LOCATION_CHANGE',
      lockActionTypes.RESET_ABORT,
    )]: () => false,
  },
  false,
);

export const error = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      lockActionTypes.UNLOCK_LOCK_REQUEST,
      lockActionTypes.UNLOCK_LOCK_SUCCESS,
      lockActionTypes.DISCARD_LOCK_REQUEST,
      lockActionTypes.DISCARD_LOCK_SUCCESS,
      lockActionTypes.ABORT_REQUEST,
      lockActionTypes.ABORT_SUCCESS,
    )]: () => ({}),
    [combineActions(
      lockActionTypes.UNLOCK_LOCK_FAILURE,
      lockActionTypes.DISCARD_LOCK_FAILURE,
      lockActionTypes.CURRENT_STATUS_FAILURE,
      lockActionTypes.ABORT_FAILURE,
    )]: (state, { payload }) => payload.error,
  },
  {},
);

export default combineReducers({
  operationInfo,
  shouldPoll,
  isLoading,
  error,
  shouldMultiPoll,
  multiStatusInfo,
  idsToPoll,
  multiPollingInterval,
  isAborting,
  abortFailure,
  shouldResetMultiPolling,
});
